import * as React from 'react';
import ReactDOM from 'react-dom';

import { Button } from '@lightspeed/flame/Button';
import { FlameTheme } from '@lightspeed/flame/Core';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { IconHelp } from '@lightspeed/flame/Icon/Help';

import { getSettingsLink } from 'root/apps/Inventory/helpers/getSettingsLink';

import { HeaderBox } from './AnalyticsLoader.style';

type HeaderProps = {
  header: React.ReactNode;
  tooltip: React.ReactNode;
  action?: React.ReactNode;
};

const SettingsLink = () => {
  return (
    <Button
      href={getSettingsLink()}
      target="_blank"
      rel="nofollow noopener noreferrer"
      id="analytics_po_settings"
    >
      <IconExternalLink /> Go to Settings
    </Button>
  );
};

const UpgradeLink = () => {
  return (
    <Button
      href="#"
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        window.Intercom?.('trackEvent', 'interested-in-analytics');
      }}
      id="analytics_subscription_po_action"
    >
      <IconExternalLink /> Upgrade
    </Button>
  );
};

const ColumnHeader: React.FC<HeaderProps> = ({ header, tooltip, action }) => {
  return (
    <FlameTheme>
      <HeaderBox>
        <div className="analytics_header">
          {header} <IconHelp baseColor="none" detailsColor1="#020203" detailsColor2="#020203" />
        </div>
        <div className="analytics_tt_container">
          <div className="analytics_tt">
            <div>{tooltip}</div>
            {action && <div className="action">{action}</div>}
          </div>
        </div>
      </HeaderBox>
    </FlameTheme>
  );
};

const setHeader = (
  selector: string,
  header: React.ReactNode,
  tooltip: React.ReactNode,
  action?: React.ReactNode
) => {
  const headerElem = document.querySelector(selector);
  if (!headerElem) return;

  ReactDOM.render(<ColumnHeader header={header} tooltip={tooltip} action={action} />, headerElem);
};

export const addAnalyticsColumnHeaders = (hasAnalytics: boolean) => {
  const action = hasAnalytics ? <SettingsLink /> : <UpgradeLink />;

  setHeader(
    'th.qty_to_order',
    <>
      Order Qty.
      <br />
    </>,
    <>
      <h2>Quantity to order</h2>
      Quantity to order is calculated using the reorder level, minus the quantities that are on
      hand, in boxes on hand, already on order or on backorder.
    </>
  );
  setHeader(
    'th.analytics_period',
    <>
      Forecast
      <br />
      Period
    </>,
    <>
      <h2>Forecast period (days)</h2>
      This is how many days you want to plan ahead for. The forecast period is based on your
      Category settings in Lightspeed Analytics.
    </>,
    action
  );
  setHeader(
    'th.analytics_level',
    <>
      Suggested
      <br />
      reorder level
    </>,
    <>
      <h2>Suggested reorder level</h2>
      This is the level of inventory you should have based on how much inventory you&rsquo;ll need
      to meet your forecasted demand.
    </>
  );
  setHeader(
    'th.analytics_need',
    <>
      Suggested
      <br />
      need
    </>,
    <>
      <h2>Suggested need</h2>
      This is how much you should reorder to meet your suggested inventory level. It&rsquo;s
      calculated using your sales history and forecast period, which you can set in Lightspeed
      Analytics. Expand Detailed view to see the calculations.
    </>,
    action
  );
};
